import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import type {
  AnnualFeeOverviewView,
  AnnualFeeRatesView,
  AnnualFeeRateView,
  SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { apiRequest } from './api';
import {
  allTeams,
  filterAnnualFeeAccounts,
  noTeam,
  parseRateInput,
  teamOptions,
  type AnnualFeeTeamFilter,
} from './annualFees';
import { amountInputText, formatMoney } from './billing';
import { errorKeyOf, type ErrorKey } from './BillingForms';
import { categoryLabel } from './teams';

export interface AnnualFeesPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
}

function rateKey(rate: Pick<AnnualFeeRateView, 'sportCode' | 'categoryCode'>): string {
  return `${rate.sportCode}:${rate.categoryCode}`;
}

/**
 * Finance's yearly fee per age group and, for the chosen calendar year, what every account owes
 * from those fees, has paid in the year and has left. Nothing here posts a charge.
 */
export function AnnualFeesPanel({
  locale,
  organizationId,
  csrfToken,
}: AnnualFeesPanelProps): React.JSX.Element {
  const base = `/api/v1/organizations/${organizationId}/billing/annual-fees`;
  const [yearText, setYearText] = useState(() => String(new Date().getFullYear()));
  const [rates, setRates] = useState<AnnualFeeRatesView | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [overview, setOverview] = useState<AnnualFeeOverviewView | null>(null);
  const [team, setTeam] = useState<AnnualFeeTeamFilter>(allTeams);
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const validYear =
    /^\d{4}$/.test(yearText) && Number(yearText) >= 2000 && Number(yearText) <= 2100;

  const showRates = useCallback((view: AnnualFeeRatesView) => {
    setRates(view);
    setDrafts(
      Object.fromEntries(
        view.rates.map((rate) => [
          rateKey(rate),
          rate.amountMinor === null ? '' : amountInputText(rate.amountMinor),
        ]),
      ),
    );
  }, []);

  const load = useCallback(
    async (year: string) => {
      try {
        const [rateView, overviewView] = await Promise.all([
          apiRequest<AnnualFeeRatesView>(`${base}/rates?year=${year}`),
          apiRequest<AnnualFeeOverviewView>(`${base}/overview?year=${year}`),
        ]);
        showRates(rateView);
        setOverview(overviewView);
      } catch (error) {
        setErrorKey(errorKeyOf(error));
      }
    },
    [base, showRates],
  );

  useEffect(() => {
    setRates(null);
    setOverview(null);
    setErrorKey(null);
    setSaved(false);
    setTeam(allTeams);
    if (validYear) void load(yearText);
  }, [load, yearText, validYear]);

  async function saveRate(rate: AnnualFeeRateView): Promise<void> {
    const amountMinor = parseRateInput(drafts[rateKey(rate)] ?? '');
    if (amountMinor === null || busy) return;
    setBusy(true);
    setErrorKey(null);
    setSaved(false);
    try {
      showRates(
        await apiRequest<AnnualFeeRatesView>(`${base}/rates`, {
          method: 'PUT',
          csrfToken,
          body: {
            year: Number(yearText),
            sportCode: rate.sportCode,
            categoryCode: rate.categoryCode,
            amountMinor,
          },
        }),
      );
      setOverview(await apiRequest<AnnualFeeOverviewView>(`${base}/overview?year=${yearText}`));
      setSaved(true);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    } finally {
      setBusy(false);
    }
  }

  const accounts = overview?.accounts ?? [];
  const shown = filterAnnualFeeAccounts(accounts, team, onlyOpen);

  return (
    <Box component="section" aria-labelledby="annual-fees-title" sx={{ mb: 3 }}>
      <Typography variant="subtitle1" component="h3" id="annual-fees-title" gutterBottom>
        {translate(locale, 'billing.annualFees.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate(locale, 'billing.annualFees.help')}
      </Typography>
      <TextField
        size="small"
        type="number"
        label={translate(locale, 'billing.annual.year')}
        value={yearText}
        disabled={busy}
        error={!validYear}
        onChange={(event) => {
          setYearText(event.target.value);
        }}
        slotProps={{ htmlInput: { min: 2000, max: 2100 } }}
        sx={{ mb: 2 }}
      />
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, 'billing.annualFees.saved')}
        </Alert>
      )}
      {rates !== null && (
        <>
          <Typography variant="subtitle2" component="h4" gutterBottom>
            {translate(locale, 'billing.annualFees.rates')}
          </Typography>
          <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, mb: 3 }}>
            {rates.rates.map((rate) => {
              const key = rateKey(rate);
              const draft = drafts[key] ?? '';
              const amountMinor = parseRateInput(draft);
              const unchanged = amountMinor !== null && amountMinor === rate.amountMinor;
              return (
                <Stack
                  component="li"
                  key={key}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ alignItems: { sm: 'center' } }}
                >
                  <Typography sx={{ minWidth: 90 }}>
                    {categoryLabel(locale, rate.categoryCode)}
                  </Typography>
                  <TextField
                    size="small"
                    label={`${translate(locale, 'billing.annual.amount')} (${rate.currency})`}
                    placeholder={translate(locale, 'billing.annualFees.notSet')}
                    value={draft}
                    disabled={busy}
                    error={draft !== '' && amountMinor === null}
                    onChange={(event) => {
                      setDrafts((current) => ({ ...current, [key]: event.target.value }));
                      setSaved(false);
                    }}
                    slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                  />
                  <Button
                    variant="outlined"
                    disabled={busy || amountMinor === null || unchanged}
                    onClick={() => {
                      void saveRate(rate);
                    }}
                  >
                    {translate(locale, 'billing.annualFees.save')}
                  </Button>
                </Stack>
              );
            })}
          </Stack>
        </>
      )}
      {overview !== null && (
        <>
          <Typography variant="subtitle2" component="h4" gutterBottom>
            {translate(locale, 'billing.annualFees.overview')}
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mb: 2, alignItems: { sm: 'center' } }}
          >
            <TextField
              select
              size="small"
              label={translate(locale, 'billing.annualFees.team')}
              value={team}
              onChange={(event) => {
                setTeam(event.target.value);
              }}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value={allTeams}>
                {translate(locale, 'billing.annualFees.allTeams')}
              </MenuItem>
              {teamOptions(accounts).map((option) => (
                <MenuItem key={option.teamSeasonId} value={option.teamSeasonId}>
                  {option.teamName}
                </MenuItem>
              ))}
              <MenuItem value={noTeam}>{translate(locale, 'billing.annualFees.noTeam')}</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={onlyOpen}
                  onChange={(event) => {
                    setOnlyOpen(event.target.checked);
                  }}
                />
              }
              label={translate(locale, 'billing.annualFees.onlyOpen')}
            />
            <Typography variant="body2" color="text.secondary">
              {translate(locale, 'billing.annualFees.shown')}: {String(shown.length)}
            </Typography>
          </Stack>
          {accounts.length === 0 && (
            <Typography>{translate(locale, 'billing.annualFees.empty')}</Typography>
          )}
          <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {shown.map((account) => (
              <Paper
                component="li"
                key={account.accountId}
                variant="outlined"
                sx={{ padding: 1.5 }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}
                >
                  <Typography
                    component={Link}
                    to={`/billing/accounts/${account.accountId}`}
                    variant="subtitle1"
                    sx={{ color: 'inherit' }}
                  >
                    {account.label}
                  </Typography>
                  {account.status === 'closed' && (
                    <Chip size="small" label={translate(locale, 'billing.accounts.closed')} />
                  )}
                </Stack>
                {account.athletes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {translate(locale, 'billing.annualFees.noAthlete')}
                  </Typography>
                ) : (
                  account.athletes.map((athlete) => (
                    <Typography key={athlete.athleteId} variant="body2" color="text.secondary">
                      {athlete.familyName} {athlete.givenName} ·{' '}
                      {athlete.team === null
                        ? translate(locale, 'billing.annualFees.noTeam')
                        : `${athlete.team.teamName} (${categoryLabel(locale, athlete.team.categoryCode)})`}
                      {athlete.feeMinor !== null &&
                        ` · ${formatMoney(athlete.feeMinor, account.currency, locale)}`}
                    </Typography>
                  ))
                )}
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {translate(locale, 'billing.annual.amount')}:{' '}
                  {formatMoney(account.feeMinor, account.currency, locale)} ·{' '}
                  {translate(locale, 'billing.annualFees.paid')}:{' '}
                  {formatMoney(account.paidMinor, account.currency, locale)} ·{' '}
                  <strong>
                    {translate(locale, 'billing.annual.remaining')}:{' '}
                    {formatMoney(account.remainingMinor, account.currency, locale)}
                  </strong>
                  {account.overpaidMinor !== '0' && (
                    <>
                      {' '}
                      · {translate(locale, 'billing.annualFees.overpaid')}:{' '}
                      {formatMoney(account.overpaidMinor, account.currency, locale)}
                    </>
                  )}
                </Typography>
                {!account.complete && account.athletes.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {translate(locale, 'billing.annualFees.incomplete')}
                  </Alert>
                )}
              </Paper>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}
